import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccountFormInput,
  AccountSchema,
  IAccountRespSchema,
  ILegalEntityResponseSchema,
} from '@monetize/types/app';
import { DEFAULT_PAGER } from '@monetize/types/common';
import isEqual from 'lodash/isEqual';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import {
  useUpdateAccount,
  useUpdateAccountAddress,
} from '../../../api/accountsService';
import { handleApiErrorToast } from '../../../api/axios';
import { useGetListData } from '../../../api/queryUtils';
import { MBox } from '../../../components/Monetize';
import { useACL } from '../../../services/acl/acl';
import { useFlags } from '../../../services/launchDarkly';
import { areAddressesSame } from '../../../utils/contact.utils';
import { nullifyEmptyStrings } from '../../../utils/misc';
import AccountForm from '../../Accounts/AccountForm';
import { SectionHeader } from '../BillGroups/components/SectionHeader';

interface AccountDetailsTabProps {
  account: IAccountRespSchema;
  refetchAccountDetails: () => void;
}

const convertAccountToFormData = (
  account: IAccountRespSchema,
): AccountFormInput => {
  return {
    accountName: account.accountName,
    accountType: account.accountType,
    isTestAccount: account.isTestAccount,
    status: account.status,
    defaultLegalEntityId: account.defaultLegalEntityId,
    defaultCurrency: account.defaultCurrency,
    defaultDunningProcessId: account.defaultDunningProcessId,
    customId: account.customId,
    ccEmails: account.ccEmails,
    bccEmails: account.bccEmails,
    taxExemptionNumber: account.taxExemptionNumber,
    taxEntityUseCode: account.taxEntityUseCode,
    customFields: account.customFields || {},
    billingAddress: account.billingAddress,
    shippingAddress: account.shippingAddress,
  };
};

export const AccountDetailsTab = ({
  account,
  refetchAccountDetails,
}: AccountDetailsTabProps) => {
  const { allowEditAccountAddress } = useFlags();
  const { canDo } = useACL();
  const canUpdateAccount = canDo([['accounts', 'update']]);

  const [searchParams, setSearchParams] = useSearchParams();
  const handleResetSearchParams = () => {
    if (searchParams.has('editMode')) {
      searchParams.delete('editMode');
      setSearchParams(searchParams);
    }
  };
  const hasEditModeSearchParams = searchParams.has('editMode');
  const [isEditMode, setIsEditMode] = useState<boolean>(
    hasEditModeSearchParams,
  );

  useEffect(() => {
    if (hasEditModeSearchParams) {
      setIsEditMode(true);
    }
  }, [hasEditModeSearchParams]);

  const convertedAccount = convertAccountToFormData(account);

  const form = useForm<AccountFormInput>({
    resolver: zodResolver(AccountSchema),
    mode: 'onChange',
    defaultValues: convertedAccount,
    values: convertedAccount,
    resetOptions: {
      keepDirtyValues: true,
      keepErrors: true,
    },
  });
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  const { data: legalEntitiesData } =
    useGetListData<ILegalEntityResponseSchema>(
      'legalEntities',
      {
        config: DEFAULT_PAGER,
      },
      {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 60 * 24,
        gcTime: 1000 * 60 * 60 * 24,
      },
    );

  const { mutateAsync: doUpdateAccount, isPending: isUpdateLoading } =
    useUpdateAccount({
      onSuccess: () => {
        setIsEditMode(false);
        handleResetSearchParams();
        refetchAccountDetails();
      },
      onError: (error) => {
        handleApiErrorToast(error);
      },
    });
  const {
    mutateAsync: doUpdateAccountAddress,
    isPending: isUpdateAddressLoading,
  } = useUpdateAccountAddress({
    onError: (error) => {
      handleApiErrorToast(error);
    },
  });

  const onSubmit = async (data: AccountFormInput) => {
    const isBillingAddressChanged = !areAddressesSame(
      account.billingAddress,
      data.billingAddress,
    );
    const isShippingAddressChanged = !areAddressesSame(
      account.shippingAddress,
      data.shippingAddress,
    );

    const nextAccountPayload = {
      ...data,
      customFields: data.customFields || {},
      billingAddress: account.billingAddress,
      shippingAddress: account.shippingAddress,
    };
    const existingAccountPayload = {
      ...convertAccountToFormData(account),
      customFields: account.customFields || {},
    };
    const normalizedNextAccountPayload = nullifyEmptyStrings({
      ...nextAccountPayload,
    });
    const normalizedExistingAccountPayload = nullifyEmptyStrings({
      ...existingAccountPayload,
    });
    const shouldUpdateAccount = !isEqual(
      normalizedNextAccountPayload,
      normalizedExistingAccountPayload,
    );
    const billingAddressPayload = data.billingAddress
      ? nullifyEmptyStrings({ ...data.billingAddress })
      : data.billingAddress;
    const shippingAddressPayload = data.shippingAddress
      ? nullifyEmptyStrings({ ...data.shippingAddress })
      : data.shippingAddress;

    if (allowEditAccountAddress && isBillingAddressChanged) {
      await doUpdateAccountAddress({
        accountId: account.id,
        type: 'billing',
        data: {
          line1: billingAddressPayload?.line1 ?? null,
          line2: billingAddressPayload?.line2 ?? null,
          city: billingAddressPayload?.city ?? null,
          state: billingAddressPayload?.state ?? null,
          postalCode: billingAddressPayload?.postalCode ?? null,
          country: billingAddressPayload?.country ?? null,
          description: billingAddressPayload?.description ?? null,
        },
      });
    }

    if (allowEditAccountAddress && isShippingAddressChanged) {
      await doUpdateAccountAddress({
        accountId: account.id,
        type: 'shipping',
        data: {
          line1: shippingAddressPayload?.line1 ?? null,
          line2: shippingAddressPayload?.line2 ?? null,
          city: shippingAddressPayload?.city ?? null,
          state: shippingAddressPayload?.state ?? null,
          postalCode: shippingAddressPayload?.postalCode ?? null,
          country: shippingAddressPayload?.country ?? null,
          description: shippingAddressPayload?.description ?? null,
        },
      });
    }

    if (shouldUpdateAccount) {
      await doUpdateAccount({
        id: account.id,
        data: nextAccountPayload,
      });
      return;
    }

    if (
      (allowEditAccountAddress && isBillingAddressChanged) ||
      (allowEditAccountAddress && isShippingAddressChanged)
    ) {
      setIsEditMode(false);
      handleResetSearchParams();
      refetchAccountDetails();
    }
  };

  return (
    <MBox pr={8}>
      <SectionHeader
        label="Account Details"
        isEditMode={isEditMode}
        isLoading={isUpdateLoading || isUpdateAddressLoading}
        canEdit={canUpdateAccount}
        onEdit={() => {
          setIsEditMode((prevState) => !prevState);
          if (isEditMode) {
            reset();
            handleResetSearchParams();
          }
        }}
        onSave={() => {
          handleSubmit(onSubmit)();
        }}
      />

      <FormProvider {...form}>
        <AccountForm
          control={control}
          setValue={setValue}
          watch={watch}
          errors={errors}
          isReadOnly={!isEditMode}
          legalEntities={legalEntitiesData?.content || []}
          allowEditAccountAddress={allowEditAccountAddress}
        />
      </FormProvider>
    </MBox>
  );
};
